/**
 * TooltipTour — Balancio product tour powered by react-joyride v3.
 * Steps come from tourSteps.ts based on the current route.
 */

import React, { useCallback, useEffect, useMemo } from 'react';
import { Joyride, EVENTS, STATUS, type EventData, type Controls } from 'react-joyride';
import { useLocation } from 'react-router-dom';
import { useTooltipTour } from './TooltipTourContext';
import { getFirstRunStepsForPath, getTourStepsForPath } from './tourSteps';
import { useAppContext } from '../contexts/Storage';
import { FROM_SETUP_KEY, isDemoSession, markOnboarding } from '../onboarding/onboardingStorage';

const TooltipTour: React.FC = () => {
  const location = useLocation();
  const { isRunning, tourMode, stopTour } = useTooltipTour();
  const { user } = useAppContext();

  useEffect(() => {
    if (!isRunning) return;
    const html = document.documentElement;
    const previousZoom = html.style.zoom;
    html.style.zoom = '1';
    return () => {
      html.style.zoom = previousZoom;
    };
  }, [isRunning]);

  const steps = useMemo(
    () =>
      tourMode === 'first-run'
        ? getFirstRunStepsForPath(location.pathname)
        : getTourStepsForPath(location.pathname),
    [location.pathname, tourMode]
  );

  const handleEvent = useCallback(
    (data: EventData, _controls: Controls) => {
      const finished =
        data.type === EVENTS.TOUR_END ||
        data.status === STATUS.FINISHED ||
        data.status === STATUS.SKIPPED;

      if (finished) {
        if (tourMode === 'first-run' && user?.id) {
          markOnboarding(user.id, 'firstRunTourDone');
          sessionStorage.removeItem(FROM_SETUP_KEY);
        }
        stopTour();
      }
    },
    [stopTour, tourMode, user?.id]
  );

  return (
    <Joyride
      steps={steps}
      run={isRunning}
      continuous
      scrollToFirstStep
      onEvent={handleEvent}
      options={{
        primaryColor: 'var(--primary)',
        backgroundColor: 'var(--light)',
        textColor: 'var(--dark)',
        arrowColor: 'var(--light)',
        overlayColor: 'rgba(0, 0, 0, 0.45)',
        zIndex: 10000,
        showProgress: true,
        buttons: ['back', 'close', 'skip', 'primary'],
        overlayClickAction: false,
      }}
      styles={{
        tooltip: {
          borderRadius: 12,
          fontFamily: 'Sora, sans-serif',
          fontSize: 14,
        },
        tooltipTitle: {
          fontFamily: 'Sora, sans-serif',
          fontSize: 16,
          fontWeight: 600,
        },
        buttonPrimary: {
          backgroundColor: 'var(--primary)',
          borderRadius: 8,
          fontFamily: 'Sora, sans-serif',
          fontSize: 13,
          padding: '8px 14px',
        },
        buttonBack: {
          color: 'var(--grey-dark)',
          fontFamily: 'Sora, sans-serif',
          fontSize: 13,
        },
        buttonSkip: {
          color: 'var(--grey-dark)',
          fontFamily: 'Sora, sans-serif',
          fontSize: 13,
        },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Done',
        next: 'Next',
        skip: 'Skip',
      }}
    />
  );
};

export default TooltipTour;
